/**
 * The entry forms' preview of how a line will be split. Mirrors ExpenseMoney in
 * server/Models/Expense.cs, which stays the authority on what gets stored - this
 * only has to agree with it on screen.
 */

function cents(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100
}

/** A car wash's price plus the tax on it, rounded as the till rounds it. 12.99 → 14.68. */
export function carWashCharged(carWashAmount, hasTax, taxRate) {
    const wash = Math.max(0, Number(carWashAmount) || 0)
    return hasTax && taxRate > 0 ? cents(wash + cents(wash * taxRate)) : wash
}

/**
 * Splits what was paid into the line's own part and a car wash part.
 *
 * The wash is entered before tax and its tax worked forward; the fuel is what
 * is left, with its tax worked back out. The tip comes off untaxed.
 */
export function splitExpense({ amountPaid, tipAmount = 0, carWashAmount = 0, hasTax, taxRate }) {
    const paid = Number(amountPaid) || 0
    const tip = Number(tipAmount) || 0

    const washPreTax = Math.max(0, Number(carWashAmount) || 0)
    const washPaid = carWashCharged(washPreTax, hasTax, taxRate)

    const mainPaid = cents(paid - washPaid)
    const taxable = Math.max(0, mainPaid - tip)
    const preTax = hasTax && taxRate > 0 ? cents(taxable / (1 + taxRate)) : taxable

    return {
        paid,
        tip,
        mainPaid,
        preTax,
        tax: cents(taxable - preTax),
        washPreTax,
        washTax: cents(washPaid - washPreTax),
        washPaid,
    }
}

/** A line whose receipt is in another currency. */
export function isForeign(values) {
    return Boolean(values.currency) && values.currency !== 'CAD'
}

/** The CAD estimate for a foreign amount - the same rounding ExchangeRateService.Estimate uses. */
export function estimateCad(originalAmount, rate, markup = 0) {
    const amount = Number(originalAmount) || 0
    return rate > 0 ? cents(amount * rate * (1 + (Number(markup) || 0))) : 0
}

/**
 * The amount fields of a line, shaped for the API.
 *
 * On a foreign receipt the server estimates the CAD itself unless the statement
 * figure is supplied, and no tax is claimed on it - so HST is always off there.
 */
export function amountFields(values) {
    if (!isForeign(values)) {
        return {
            currency: 'CAD',
            originalAmount: null,
            amountPaid: Number(values.amountPaid),
            cadConfirmed: false,
            hasTax: values.hasTax,
        }
    }
    return {
        currency: values.currency,
        originalAmount: Number(values.originalAmount),
        amountPaid: values.cadConfirmed ? Number(values.amountPaid) || 0 : 0,
        cadConfirmed: values.cadConfirmed === true,
        hasTax: false,
    }
}

/** What is wrong with the amount fields of a line, or null. */
export function amountProblem(values) {
    if (!isForeign(values)) {
        const amount = Number(values.amountPaid)
        return Number.isFinite(amount) && amount > 0 ? null : 'Enter a valid amount greater than zero'
    }

    const original = Number(values.originalAmount)
    if (!Number.isFinite(original) || original <= 0) {
        return `Enter the total printed on the receipt, in ${values.currency}`
    }
    if (values.cadConfirmed) {
        const cad = Number(values.amountPaid)
        if (!Number.isFinite(cad) || cad <= 0) return 'Enter the CAD amount from the card statement, or untick it'
    }
    return null
}

/**
 * The problem with a car wash amount, or null. The wash with its tax has to
 * leave something for the fuel - otherwise the receipt total was probably
 * typed into the wash box.
 */
export function carWashProblem({ amountPaid, tipAmount = 0, carWashAmount, hasTax, taxRate }) {
    const raw = String(carWashAmount ?? '').trim()
    const wash = Number(raw)
    if (!raw || !Number.isFinite(wash) || wash <= 0) return 'Enter the car wash price before tax, or untick car wash'

    const left = (Number(amountPaid) || 0) - (Number(tipAmount) || 0)
    if (carWashCharged(wash, hasTax, taxRate) >= left) {
        return 'The car wash with tax must be less than the amount paid — enter the whole receipt as the amount paid'
    }
    return null
}
